/**
 * When the story build is allowed to start.
 *
 * Split out of `useStoryStage` because it is pure geometry and it was wrong in
 * a way only arithmetic can show. The hook is otherwise untestable — it needs a
 * laid-out DOM, an IntersectionObserver and a font-loading promise — so the one
 * rule that decides whether the landing page's flagship demo ever plays had no
 * test at all.
 *
 * ## The defect this replaced
 *
 * The old rule had two branches:
 *
 *   if (height <= viewport - 4) return top >= -2 && bottom <= viewport + 2;
 *   return visibleHeight >= viewport * 0.85;
 *
 * The first branch demands the element be COMPLETELY inside the viewport. It
 * was written for the band (1119px, which on a 900px window takes the second
 * branch), but it is applied to the SCROLLER — 744px — which takes the first.
 * So on a 900px window the stage armed only while its top sat in a 156px band
 * of scroll positions, and outside that window the build never started: an
 * unarmed stage is paused at frame 0, and frame 0 of every element in it is
 * `opacity: 0`. The page's centrepiece rendered as an empty black rectangle
 * ~744px tall.
 *
 * Measured, not inferred: a `scrollTo` that left the scroller at top 344 in a
 * 900px viewport reported `data-armed="0"` and eight children at opacity 1
 * inside a stage that showed nothing, indefinitely.
 *
 * Two things had to change:
 *
 *  - **One rule, not two.** A coverage RATIO of the element's own height can
 *    never demand more than the viewport is able to give, which is exactly the
 *    failure the first branch had. 70% is the threshold: enough that the build
 *    does not start off-screen, loose enough that it starts as the stage
 *    arrives rather than after it has fully landed.
 *  - **A visibility gate needs a fallback.** The observer and the scroll
 *    listener both report CHANGES. A visitor who arrives already parked on the
 *    band — a reload with restored scroll, an `#story` anchor, one Page Down —
 *    generates no further events, so a gate that answered "not yet" once
 *    answered it forever. `hasArrived` is the second, weaker question the
 *    fallback asks after a bounded wait: is any of the stage on screen at all.
 */

export interface StageRect {
  readonly top: number;
  readonly height: number;
}

/** Fraction of the stage that must be on screen before the build starts. */
export const ARM_COVERAGE = 0.7;

/** How long to wait before falling back to the weaker `hasArrived` test. */
export const ARM_FALLBACK_MS = 2500;

/** The fraction of the stage the fallback settles for. */
const ARRIVED_COVERAGE = 0.25;

function coverage(rect: StageRect, viewportHeight: number): number {
  if (viewportHeight <= 0 || rect.height <= 0) return 0;
  const visible = Math.min(rect.top + rect.height, viewportHeight) - Math.max(rect.top, 0);
  return Math.max(0, visible) / rect.height;
}

/** The primary gate: most of the stage is on screen, so the build can start. */
export function isStageInView(rect: StageRect, viewportHeight: number): boolean {
  return coverage(rect, viewportHeight) >= ARM_COVERAGE;
}

/**
 * The fallback gate, used only after `ARM_FALLBACK_MS`: the visitor is looking
 * at the band even if it is not well framed. Deliberately weaker rather than
 * unconditional — arming a stage that is nowhere near the viewport would replay
 * the original bug from the other side, spending the build before anyone can
 * see it.
 */
export function hasStageArrived(rect: StageRect, viewportHeight: number): boolean {
  return coverage(rect, viewportHeight) >= ARRIVED_COVERAGE;
}
